import {
  afterRenderEffect,
  Component,
  computed,
  contentChild,
  DestroyRef,
  inject,
  input,
  output,
  Signal,
  signal,
  TemplateRef,
  viewChild
} from "@angular/core";
import {
  ContainerBaseComponent,
  ContainerLoadFn,
  ContainerNoDataFn
} from "src/app/components/ui/container/container-base/container-base.component";
import { ContainerContentDirective } from "src/app/components/ui/container/directives/container-content.directive";
import { TabService, TabServiceTab } from "src/app/services/tab.service";
import { AbilityAction, AbilitySubject } from "src/app/services/user.service";
import { SseEvents } from "src/app/sse/sse-events";
import { CURRENT_TAB_FACTORY } from "src/app/tokens/current-tab.token";

@Component({
  standalone: false,
  selector: "pms-container-tab",
  templateUrl: "container-tab.component.html",
  styles: `
    :host {
      display: flex;
      flex: 1;
    }
  `
})
export class ContainerTabComponent {
  loaderType = input<"spinner" | "skeleton-page-detail">(
    "skeleton-page-detail"
  );

  able = input<Array<[AbilityAction, AbilitySubject, any?]> | undefined>(
    undefined
  );

  load = input<ContainerLoadFn<any> | undefined>(undefined);

  loadParams = input<any | undefined>(undefined);

  noData = input<ContainerNoDataFn<any> | null>(null);

  loaderCssClass = input<string | undefined>(undefined);

  tab = input<TabServiceTab | undefined>(undefined);

  brokerEvents = input<SseEvents[] | undefined>(undefined);

  template = input<TemplateRef<unknown> | undefined>(undefined);

  contentTemplateRef = contentChild(ContainerContentDirective);

  computedTemplateRef: Signal<TemplateRef<unknown> | undefined> = computed(
    () => {
      const templateRef: TemplateRef<unknown> | undefined = this.template();
      const contentTemplateRef: ContainerContentDirective | undefined =
        this.contentTemplateRef();

      return templateRef || contentTemplateRef?.templateRef;
    }
  );

  readonly injectedTab = signal<TabServiceTab | undefined>(
    inject(CURRENT_TAB_FACTORY)()
  );

  readonly effectiveTab: Signal<TabServiceTab | undefined> = computed(() => {
    const override: TabServiceTab | undefined = this.tab();

    return override ?? this.injectedTab();
  });

  data = output<any>();

  readonly containerBase = viewChild(ContainerBaseComponent);

  readonly #tabService = inject(TabService);

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterRenderEffect(() => {
      const containerBase = this.containerBase();
      const tabForListener: TabServiceTab | undefined = this.effectiveTab();

      if (!containerBase || !tabForListener) {
        return;
      }

      const activatedId = this.#tabService.addEventListener({
        tab: tabForListener,
        type: "activationEnd",
        handler: () => {
          containerBase.refreshSilent();
        }
      });

      destroyRef.onDestroy(() => {
        this.#tabService.removeEventListener(activatedId);
      });
    });
  }

  async refresh(): Promise<void> {
    await this.containerBase()?.refresh();
  }

  async refreshSilent(): Promise<void> {
    await this.containerBase()?.refreshSilent();
  }

  startLoading(): void {
    this.containerBase()?.startLoading();
  }
}
